import React from "react";
import { User, ClipboardCheck } from "lucide-react";

// Panneau fixe (indépendant) affiché à gauche de l'entrevue : identité du
// client + questionnaire de vérification en lecture seule. Alimenté uniquement
// par `fir.answers` (aucun appel réseau). Se ferme avec l'entrevue.

const QUESTIONS_VERIF = [
  ["nouveau_client", "Nouveau client du réseau de l'aide juridique ?"],
  ["admis_12_mois", "Admis à l'aide juridique dans les 12 derniers mois ?"],
  ["financiere_inchangee", "Situation financière inchangée ?"],
  ["sante_mentale", "Troubles de santé mentale ?"],
  ["detenu", "Détenu ?"],
  ["vcvs", "Contexte de VCVS ?"],
  ["premieres_nations", "Membre des Premières Nations ?"],
  ["immigration", "Demande en droit de l'immigration ?"],
  ["cour_itinerante", "Dossier à la Cour itinérante ?"],
  ["itinerance", "En situation d'itinérance ?"],
  ["aide_sociale", "Prestations d'aide sociale ou de solidarité sociale (exclut PRB et objectif emploi) ?"],
  ["tiers_impossibilite", "Demande au nom d'un tiers dans l'impossibilité d'agir (art. 30 al.1) ?"],
  ["benefice_autrui", "Demande pour un service au bénéfice d'autrui (art. 30 al.2) ?"],
];

function computeAge(ddn) {
  if (!ddn) return null;
  const d = new Date(ddn);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
  return a;
}

const Row = ({ label, value }) => {
  if (value === undefined || value === null || String(value).trim() === "") return null;
  return (
    <div className="ecp-row">
      <span className="ecp-row-label">{label}</span>
      <span className="ecp-row-value">{value}</span>
    </div>
  );
};

export default function EntrevueClientPanel({ fir, answers }) {
  const a = answers || {};
  const nom = (a.Nom || fir.client_nom || "").trim();
  const prenom = (a.Pr_nom || fir.client_prenom || "").trim();
  const ddn = a.Date_de_naissance1 || fir.client_date_naissance || "";
  const age = computeAge(ddn);

  const villeLigne = [a.Ville, a.Province, a.Code_postal].filter(Boolean).join(", ");
  const adresseLigne = [a.Adresse, a.N_Appartement1 ? `app. ${a.N_Appartement1}` : ""].filter(Boolean).join(" ");

  const q = a._questionnaire || null;
  const estMineur = q && q.mineur;
  const aQuestionnaire = q && !q.mineur;

  return (
    <aside className="entrevue-side-panel" data-testid="entrevue-client-panel">
      <div className="ecp-header">
        <User size={15} strokeWidth={2.4} />
        <span>Dossier en cours</span>
      </div>

      <div className="ecp-scroll">
        {/* Identité client */}
        <div className="ecp-section">
          <div className="ecp-name" data-testid="ecp-client-nom">
            {[nom.toUpperCase(), prenom].filter(Boolean).join(", ") || "—"}
          </div>
          <Row label="Date de naissance" value={ddn ? `${ddn}${age != null ? ` (${age} ans)` : ""}` : ""} />
          <Row label="Sexe" value={a.Sexe} />
          <Row label="N.A.S." value={a.Num_ro_d_assurance_sociale1} />
          <Row label="No client" value={a._noclient} />
          <Row label="No demande" value={fir.daj_noseqdaj} />
        </div>

        {(adresseLigne || villeLigne || a.T_l_phone || a.T_l_phone_cellulaire || a.T_l_phone_autre || a.Adresse_courriel) && (
          <div className="ecp-section">
            <div className="ecp-subtitle">Coordonnées</div>
            <Row label="Adresse" value={adresseLigne} />
            <Row label="Ville" value={villeLigne} />
            <Row label="Tél. résidence" value={a.T_l_phone} />
            <Row label="Cellulaire" value={a.T_l_phone_cellulaire} />
            <Row label="Autre tél." value={a.T_l_phone_autre} />
            <Row label="Courriel" value={a.Adresse_courriel} />
          </div>
        )}

        {a.Nom_Pr_nom_et_DDN_du_conjoint_e && (
          <div className="ecp-section">
            <div className="ecp-subtitle">Conjoint(e)</div>
            <Row label="Identité" value={a.Nom_Pr_nom_et_DDN_du_conjoint_e} />
          </div>
        )}

        {/* Questionnaire de vérification (lecture seule) */}
        <div className="ecp-section">
          <div className="ecp-subtitle" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ClipboardCheck size={13} strokeWidth={2.4} />
            Questionnaire de vérification
          </div>

          {estMineur ? (
            <div className="ecp-empty" data-testid="ecp-questionnaire-mineur">
              Client(e) de moins de 18 ans — aucun questionnaire de vérification requis.
            </div>
          ) : !aQuestionnaire ? (
            <div className="ecp-empty" data-testid="ecp-questionnaire-absent">
              Aucun questionnaire de vérification.
            </div>
          ) : (
            <div data-testid="ecp-questionnaire">
              {QUESTIONS_VERIF.map(([k, label]) => {
                const oui = !!q[k];
                return (
                  <div key={k} className="ecp-q">
                    <span className="ecp-q-label">{label}</span>
                    <span className={`ecp-q-answer ${oui ? "oui" : "non"}`} data-testid={`ecp-q-${k.replace(/_/g, "-")}`}>
                      {oui ? "Oui" : "Non"}
                    </span>
                  </div>
                );
              })}
              <div className="ecp-decision" data-testid="ecp-decision">
                <Row label="Catégorie" value={q.cat_id != null ? `Catégorie ${q.cat_id}` : null} />
                <div className="ecp-row">
                  <span className="ecp-row-label">Sélection</span>
                  <span className={`ecp-selection ${q.demande_docs ? "sel" : "nonsel"}`}>
                    {q.demande_docs ? "Sélectionné pour vérification" : "Non sélectionné"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
